import { CSSProperties, useMemo } from "react"
import styles from "./PublicAnimatedBackground.module.css"

const fallbackPrimaryColor = "#f97316"
const fallbackAccentColor = "#dc2626"
const fallbackSecondaryColor = "#111827"
const fallbackBaseColor = "#ffffff"
const fallbackTextColor = "#0f172a"

const normalizeHexColor = (color?: string) => {
  if (!color) return ""

  const trimmedColor = color.trim()

  if (/^#[0-9a-fA-F]{6}$/.test(trimmedColor)) {
    return trimmedColor
  }

  if (/^#[0-9a-fA-F]{3}$/.test(trimmedColor)) {
    const [, red, green, blue] = trimmedColor

    return `#${red}${red}${green}${green}${blue}${blue}`
  }

  return ""
}

const hexToRgb = (color?: string) => {
  const normalizedColor = normalizeHexColor(color)

  if (!normalizedColor) return null

  return {
    red: Number.parseInt(normalizedColor.slice(1, 3), 16),
    green: Number.parseInt(normalizedColor.slice(3, 5), 16),
    blue: Number.parseInt(normalizedColor.slice(5, 7), 16),
  }
}

const toRgba = (color: string | undefined, alpha: number, fallback: string) => {
  const rgb = hexToRgb(color) ?? hexToRgb(fallback)

  if (!rgb) return fallback

  return `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${alpha})`
}

const isDarkColor = (color: string | undefined) => {
  const rgb = hexToRgb(color)

  if (!rgb) return false

  return (rgb.red * 299 + rgb.green * 587 + rgb.blue * 114) / 1000 < 128
}

interface PublicAnimatedBackgroundProps {
  baseColor?: string
}

export const PublicAnimatedBackground = ({ baseColor = fallbackBaseColor }: PublicAnimatedBackgroundProps) => {
  const customProperties = useMemo(() => {
    const isDark = isDarkColor(baseColor)

    return {
      "--public-background-base": baseColor,
      "--public-background-primary-glow": toRgba(fallbackPrimaryColor, isDark ? 0.24 : 0.13, fallbackPrimaryColor),
      "--public-background-accent-glow": toRgba(fallbackAccentColor, isDark ? 0.2 : 0.1, fallbackAccentColor),
      "--public-background-secondary-glow": toRgba(fallbackSecondaryColor, isDark ? 0.16 : 0.08, fallbackSecondaryColor),
      "--public-background-grid-line": toRgba(fallbackSecondaryColor, isDark ? 0.14 : 0.1, fallbackTextColor),
    } as CSSProperties
  }, [baseColor])

  return <span className={styles.background} style={customProperties} aria-hidden="true" />
}

export const publicAnimatedBackgroundShellClassName = styles.shell
